/**
 * Querri Embed SDK
 *
 * Embeds the Querri application into a customer's website via iframe.
 * Zero dependencies, vanilla JavaScript.
 */

var STORAGE_KEY_PREFIX = 'querri_embed_session_';

// ─── Helpers ──────────────────────────────────────────────

function resolveElement(selectorOrElement) {
  if (typeof selectorOrElement === 'string') {
    var el = document.querySelector(selectorOrElement);
    if (!el) throw new Error('QuerriEmbed: element not found: ' + selectorOrElement);
    return el;
  }
  if (selectorOrElement instanceof HTMLElement) return selectorOrElement;
  throw new Error('QuerriEmbed: invalid container — pass a CSS selector or HTMLElement');
}

function parseOrigin(url) {
  try {
    var a = document.createElement('a');
    a.href = url;
    return a.protocol + '//' + a.host;
  } catch (e) {
    return url;
  }
}

function storageKey(serverUrl) {
  return STORAGE_KEY_PREFIX + parseOrigin(serverUrl);
}

function getCachedToken(serverUrl) {
  try {
    return localStorage.getItem(storageKey(serverUrl)) || null;
  } catch (e) {
    return null;
  }
}

function setCachedToken(serverUrl, token) {
  try {
    localStorage.setItem(storageKey(serverUrl), token);
  } catch (e) { /* quota exceeded or private mode */ }
}

function clearCachedToken(serverUrl) {
  try {
    localStorage.removeItem(storageKey(serverUrl));
  } catch (e) { /* ignore */ }
}

// ─── Overlay (login prompt) ───────────────────────────────

function createOverlay(container) {
  var overlay = document.createElement('div');
  overlay.style.cssText =
    'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
    'background:#f8f9fa;z-index:10;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';

  var card = document.createElement('div');
  card.style.cssText =
    'text-align:center;padding:40px;background:#fff;border-radius:12px;' +
    'box-shadow:0 2px 12px rgba(0,0,0,0.08);max-width:360px;width:100%;';

  var title = document.createElement('div');
  title.style.cssText = 'font-size:16px;font-weight:500;color:#1a1a1a;margin-bottom:20px;';
  title.textContent = 'Sign in to view this content';

  var btn = document.createElement('button');
  btn.style.cssText =
    'display:inline-block;padding:10px 24px;background:#3b82f6;color:#fff;border:none;' +
    'border-radius:8px;font-size:14px;font-weight:500;cursor:pointer;transition:background 0.2s;';
  btn.textContent = 'Sign in with Querri';
  btn.onmouseover = function () { btn.style.background = '#2563eb'; };
  btn.onmouseout = function () { btn.style.background = '#3b82f6'; };

  var footer = document.createElement('div');
  footer.style.cssText = 'margin-top:16px;font-size:11px;color:#9ca3af;';
  footer.textContent = 'Powered by Querri';

  card.appendChild(title);
  card.appendChild(btn);
  card.appendChild(footer);
  overlay.appendChild(card);
  container.appendChild(overlay);

  return { overlay: overlay, button: btn };
}

// ─── Loading spinner ──────────────────────────────────────

function createLoader(container) {
  var loader = document.createElement('div');
  loader.style.cssText =
    'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;' +
    'background:#f8f9fa;z-index:5;';

  var spinner = document.createElement('div');
  spinner.style.cssText =
    'width:32px;height:32px;border:3px solid #e5e7eb;border-top-color:#3b82f6;' +
    'border-radius:50%;animation:querri-spin 0.8s linear infinite;';

  // Inject keyframes if not already present
  if (!document.getElementById('querri-embed-styles')) {
    var style = document.createElement('style');
    style.id = 'querri-embed-styles';
    style.textContent = '@keyframes querri-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  loader.appendChild(spinner);
  container.appendChild(loader);
  return loader;
}

// ─── Auth classification ─────────────────────────────────

var INVALID_AUTH_SHAPE_MSG =
  'auth must be "login", { shareKey, org }, { sessionEndpoint }, or { fetchSessionToken }';

function classifyAuth(auth) {
  if (auth === 'login') return { mode: 'login' };
  if (!auth || typeof auth !== 'object') {
    return { mode: 'invalid', message: INVALID_AUTH_SHAPE_MSG };
  }

  if ('shareKey' in auth) {
    if (typeof auth.shareKey !== 'string' || !auth.shareKey) {
      return { mode: 'invalid', message: 'auth.shareKey must be a non-empty string' };
    }
    if (!auth.org || typeof auth.org !== 'string') {
      return {
        mode: 'invalid',
        message:
          'auth.org is required when using shareKey authentication. ' +
          'Pass { shareKey: "...", org: "your-org-id" }.',
      };
    }
    return { mode: 'shareKey', shareKey: auth.shareKey, org: auth.org };
  }

  if ('fetchSessionToken' in auth) {
    if (typeof auth.fetchSessionToken !== 'function') {
      return {
        mode: 'invalid',
        message:
          'auth.fetchSessionToken must be a function that returns a Promise<string>. ' +
          'Got ' + typeof auth.fetchSessionToken + '.',
      };
    }
    return { mode: 'fetchToken', fetchToken: auth.fetchSessionToken };
  }

  if ('sessionEndpoint' in auth) {
    if (typeof auth.sessionEndpoint !== 'string' || !auth.sessionEndpoint) {
      return { mode: 'invalid', message: 'auth.sessionEndpoint must be a non-empty string' };
    }
    var endpoint = auth.sessionEndpoint;
    return {
      mode: 'fetchToken',
      fetchToken: function () {
        return fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).then(function (res) {
          if (!res.ok) return sessionEndpointError(res);
          return res.json();
        }).then(function (data) {
          return data.session_token;
        });
      },
    };
  }

  return { mode: 'invalid', message: INVALID_AUTH_SHAPE_MSG };
}

/**
 * Turn a non-OK session-endpoint response into a thrown Error that keeps the
 * server SDK handler's JSON (`{ error, code, permission, reason }`) on
 * `err.detail`, so a refused role (e.g. `role_disabled`) is visible in the
 * `token_fetch_failed` event instead of a bare status.
 *
 * The Nuxt handler throws h3's `createError({ data })`, which answers
 * `{ statusCode, statusMessage, data: { error, code, ... } }` -- so when the
 * top level has no `code`, the fields are read from `body.data`.
 *
 * The error is marked `_querriSessionEndpoint` so `_handleFetchToken` forwards
 * only this `detail`, never one a custom `fetchSessionToken` attached.
 */
function sessionEndpointError(res) {
  return res.json().catch(function () { return null; }).then(function (body) {
    var detail = { status: res.status };
    if (body && typeof body === 'object' && typeof body.code !== 'string' &&
        body.data && typeof body.data === 'object') {
      body = body.data;
    }
    if (body && typeof body === 'object') {
      if (typeof body.code === 'string') detail.code = body.code;
      if (typeof body.permission === 'string') detail.permission = body.permission;
      if (typeof body.reason === 'string') detail.reason = body.reason;
    }
    var err = new Error(
      'Session endpoint returned ' + res.status + (detail.code ? ' (' + detail.code + ')' : '')
    );
    err.detail = detail;
    err._querriSessionEndpoint = true;
    throw err;
  });
}

// ─── Instance ─────────────────────────────────────────────

function QuerriInstance(container, options) {
  this._container = container;
  this._options = options;
  this._serverUrl = options.serverUrl.replace(/\/+$/, '');
  this._origin = parseOrigin(options.serverUrl);
  // How long to wait for the iframe's `ready` before saying something is wrong.
  // This clock covers the WHOLE cold start — document fetch, the app bundle,
  // parse, module evaluation — not just the handshake. Measured on a warm
  // localhost dev server that is 4.2-4.8s; on Fast 3G it was 24.8s. The old 15s
  // budget was below a real-world load on a slow connection and reported a
  // failure for embeds that went on to work — so it is 30s now, the error is
  // `recoverable: true`, and it is RETRACTED (the `recovered` event) when
  // `ready` shows up late. `readyTimeout: 0` disables the warning entirely.
  // `timeout` survives as a deprecated alias (removal in the next major).
  var readyTimeout = options.readyTimeout != null ? options.readyTimeout : options.timeout;
  this._readyTimeout = (typeof readyTimeout === 'number' && readyTimeout >= 0) ? readyTimeout : 30000;
  this._timedOut = false;
  this._createdAt = Date.now();
  this._pendingPrompts = [];
  this._promptSeq = 0;
  this._listeners = {};
  this._messageHandler = null;
  this._popupMessageHandler = null;
  this._popup = null;
  this._overlay = null;
  this._loader = null;
  this._pollTimer = null;
  this._readyTimer = null;
  this._retryTimer = null;
  this._fetchInFlight = false;
  this._fetchCycleCount = 0;
  this._destroyed = false;
  // Flips true the first time the iframe confirms authentication. Gates
  // startView in _buildConfig so that re-init messages triggered by
  // session-expired / auth-required do not yank the user back to startView
  // after they've navigated within the embed.
  this._hasAuthenticated = false;

  this.iframe = null;
  this.ready = false;

  this._init();
}

QuerriInstance.prototype._init = function () {
  var self = this;

  // Ensure container has relative positioning for overlay
  var pos = getComputedStyle(this._container).position;
  if (pos === 'static' || pos === '') {
    this._container.style.position = 'relative';
  }

  // Warn if container has no visible dimensions (deferred to after CSS layout)
  requestAnimationFrame(function () {
    if (self._destroyed) return;
    var rect = self._container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn(
        'QuerriEmbed: container has zero ' + (rect.width === 0 ? 'width' : 'height') +
        '. The embed will be invisible. Set explicit width/height on the container element.'
      );
    }
  });

  // Show loading spinner
  this._loader = createLoader(this._container);

  // Validation + mode selection in one pass; invalid shapes defer an error
  // so callers can attach handlers after create() returns.
  var classified = classifyAuth(this._options.auth);
  switch (classified.mode) {
    case 'shareKey':
      this._initShareKey(classified);
      break;
    case 'fetchToken':
      this._initFetchToken(classified.fetchToken);
      break;
    case 'login':
      this._initPopupLogin();
      break;
    case 'invalid':
      if (this._loader) { this._loader.remove(); this._loader = null; }
      this._deferError('invalid_auth', classified.message);
      break;
  }
};

// ─── Mode 1: Share Key ────────────────────────────────────

QuerriInstance.prototype._initShareKey = function (classified) {
  var url = this._serverUrl + '/embed?share=' + encodeURIComponent(classified.shareKey) +
    '&org=' + encodeURIComponent(classified.org);
  // Only when the caller asked for one: unset means the runtime's own default
  // (the home launcher), same as the served asset — forcing '/home' here made
  // the two SDK lineages land omitted-startView embeds on different views.
  if (this._options.startView) {
    url += '&startView=' + encodeURIComponent(this._options.startView);
  }
  this._createIframe(url);
  this._setupMessageListener();

  var self = this;
  this._authStrategy = {
    onReady: function () {
      self._sendToIframe({ type: 'init', config: self._buildConfig() });
    },
    onAuthRequired: function () {
      self._emitError(
        'auth_required',
        'The embed could not authenticate. In share-key mode check that both ' +
          '`shareKey` and `org` are set; otherwise configure `auth: "login"` ' +
          'or `auth.fetchSessionToken`.'
      );
    },
    onSessionExpired: function () { /* share-key has no re-auth path */ },
  };
};

// ─── Mode 2: fetchSessionToken ────────────────────────────

QuerriInstance.prototype._initFetchToken = function (fetchTokenFn) {
  this._createIframe(this._serverUrl + '/embed');
  this._setupMessageListener();

  this._fetchTokenFn = fetchTokenFn;

  var self = this;
  this._authStrategy = {
    onReady: function () { self._handleFetchToken(); },
    onAuthRequired: function () { self._handleFetchToken(); },
    onSessionExpired: function () { self._handleFetchToken(); },
  };
};

QuerriInstance.prototype._handleFetchToken = function () {
  var self = this;
  if (!this._fetchTokenFn) return;

  // Deduplication: skip if a fetch is already in progress
  if (this._fetchInFlight) return;

  // Circuit breaker: stop if we've exhausted all re-fetch cycles
  var maxCycles = 3;
  if (this._fetchCycleCount >= maxCycles) {
    this._emitError('token_fetch_exhausted',
      'Token fetch circuit breaker: exceeded ' + maxCycles +
      ' fetch cycles. Call destroy() and re-create the embed, or check your fetchSessionToken implementation.'
    );
    return;
  }
  this._fetchCycleCount++;

  this._fetchInFlight = true;

  var maxRetries = 3;
  var attempt = 0;

  function tryFetch() {
    attempt++;
    Promise.resolve(self._fetchTokenFn()).then(function (token) {
      if (self._destroyed) return;
      if (!token || typeof token !== 'string') {
        throw new Error(
          'fetchSessionToken must return a non-empty string, got: ' + typeof token
        );
      }
      self._fetchInFlight = false;
      self._sendToIframe({
        type: 'init',
        sessionToken: token,
        config: self._buildConfig(),
      });
    }).catch(function (err) {
      if (self._destroyed) return;
      // A session endpoint's 4xx is the host's (or Querri's) deliberate
      // answer -- e.g. 403 role_disabled -- and asking again re-runs the
      // host's getSession() for the same refusal. Only 408 and 429 are worth
      // retrying; 5xx and network errors still are.
      var status = err && err._querriSessionEndpoint && err.detail ? err.detail.status : 0;
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        self._fetchInFlight = false;
        self._emitError('token_fetch_failed',
          'Failed to fetch session token: ' + (err.message || 'Unknown error'),
          err.detail
        );
        return;
      }
      if (attempt < maxRetries) {
        var delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s
        self._retryTimer = setTimeout(function () {
          self._retryTimer = null;
          if (!self._destroyed) tryFetch();
        }, delay);
      } else {
        self._fetchInFlight = false;
        self._emitError('token_fetch_failed',
          'Failed to fetch session token after ' + maxRetries + ' attempts: ' +
          (err.message || 'Unknown error'),
          err && err._querriSessionEndpoint ? err.detail : undefined
        );
      }
    });
  }

  tryFetch();
};

// ─── Mode 3: Popup Login ─────────────────────────────────

QuerriInstance.prototype._initPopupLogin = function () {
  var cached = getCachedToken(this._serverUrl);

  this._createIframe(this._serverUrl + '/embed');
  this._setupMessageListener();

  if (cached) {
    // Try cached token — iframe will tell us if it's valid.
    // If invalid, we'll receive 'auth-required' and show the overlay then.
    this._pendingCachedToken = cached;
  }

  var self = this;
  this._authStrategy = {
    onReady: function () {
      if (self._pendingCachedToken) {
        self._sendToIframe({
          type: 'init',
          sessionToken: self._pendingCachedToken,
          config: self._buildConfig(),
        });
        self._pendingCachedToken = null;
      } else {
        self._sendToIframe({ type: 'init', config: self._buildConfig() });
      }
    },
    onAuthRequired: function () {
      clearCachedToken(self._serverUrl);
      self._showLoginOverlay();
    },
    onSessionExpired: function () {
      self._showLoginOverlay();
    },
  };
};

QuerriInstance.prototype._showLoginOverlay = function () {
  var self = this;
  if (this._overlay) return;

  // Hide loader, show overlay
  if (this._loader) this._loader.style.display = 'none';

  var ui = createOverlay(this._container);
  this._overlay = ui.overlay;

  ui.button.onclick = function () {
    self._openPopup();
  };
};

QuerriInstance.prototype._hideOverlay = function () {
  if (this._overlay) {
    this._overlay.remove();
    this._overlay = null;
  }
};

QuerriInstance.prototype._openPopup = function () {
  // Guard against multiple concurrent popups (e.g. double-click)
  if (this._popup && !this._popup.closed) return;

  var self = this;
  var w = 500, h = 650;
  var left = (screen.width - w) / 2;
  var top = (screen.height - h) / 2;

  // Pass the opener's origin so the popup can target postMessage securely
  var popupUrl = this._serverUrl + '/auth/embed-login?opener_origin=' +
    encodeURIComponent(window.location.origin);

  this._popup = window.open(
    popupUrl,
    'querri_embed_login',
    'width=' + w + ',height=' + h + ',left=' + left + ',top=' + top + ',toolbar=no,menubar=no'
  );

  // Detect popup blocked by browser
  if (!this._popup) {
    this._emitError('popup_blocked', 'Login popup was blocked by the browser. Please allow popups for this site.');
    return;
  }

  // Listen for popup postMessage
  if (!this._popupMessageHandler) {
    this._popupMessageHandler = function (e) {
      if (e.origin !== self._origin) return;
      if (!e.data || !e.data.type) return;

      if (e.data.type === 'querri-auth-success' && e.data.sessionToken) {
        setCachedToken(self._serverUrl, e.data.sessionToken);

        // Send token to iframe
        self._sendToIframe({
          type: 'auth-session',
          sessionToken: e.data.sessionToken,
        });

        self._hideOverlay();
        if (self._popup) {
          self._popup.close();
          self._popup = null;
        }
      } else if (e.data.type === 'querri-auth-error') {
        self._emitError('auth_failed', e.data.error || 'Authentication failed');
      }
    };
    window.addEventListener('message', this._popupMessageHandler);
  }

  // Poll for popup close (user may close manually)
  if (this._pollTimer) clearInterval(this._pollTimer);
  this._pollTimer = setInterval(function () {
    if (self._destroyed || !self._popup) {
      clearInterval(self._pollTimer);
      self._pollTimer = null;
      self._popup = null;
      return;
    }
    try {
      if (self._popup.closed) {
        clearInterval(self._pollTimer);
        self._pollTimer = null;
        self._popup = null;
      }
    } catch (e) {
      // COOP blocks cross-origin window.closed access.
      // Stop polling — auth completion comes via postMessage instead.
      clearInterval(self._pollTimer);
      self._pollTimer = null;
    }
  }, 500);
};

// ─── iframe Management ────────────────────────────────────

QuerriInstance.prototype._createIframe = function (src) {
  var self = this;

  // Warn if container already has a QuerriEmbed iframe
  var existing = this._container.querySelector('iframe[src*="/embed"]');
  if (existing) {
    console.warn(
      'QuerriEmbed: container already has a QuerriEmbed iframe. ' +
      'Call destroy() on the existing instance first.'
    );
  }

  var iframe = document.createElement('iframe');
  // Declare who the parent is IN THE URL. The runtime used to pin its trust
  // anchor to whichever origin sent it the first message, so a script that beat
  // this SDK to the handshake set that anchor. Passing it up front lets the
  // runtime validate against a value it knew before anyone spoke to it.
  iframe.src = src + (src.indexOf('?') === -1 ? '?' : '&') +
    'parentOrigin=' + encodeURIComponent(window.location.origin);
  iframe.style.cssText = 'border:none;width:100%;height:100%;display:block;';
  iframe.setAttribute('referrerpolicy', 'strict-origin');
  iframe.allow = 'clipboard-write';
  this._container.appendChild(iframe);
  this.iframe = iframe;

  // Warn if the iframe has not announced itself in time. `recoverable: true`
  // because a slow connection is the common cause and the embed usually still
  // arrives — a host that treats this as terminal shows its users an error for
  // a page that is about to work. Paired with the `recovered` event.
  if (this._readyTimeout > 0) {
    this._readyTimer = setTimeout(function () {
      self._readyTimer = null;
      if (self._destroyed || self.ready) return;
      self._timedOut = true;
      self._emit('error', {
        code: 'timeout',
        recoverable: true,
        message: 'Embed iframe has not responded after ' + self._readyTimeout +
          'ms — it may still be loading',
      });
    }, this._readyTimeout);
  }
};

QuerriInstance.prototype._sendToIframe = function (msg) {
  if (this.iframe && this.iframe.contentWindow) {
    this.iframe.contentWindow.postMessage(msg, this._origin);
  }
};

QuerriInstance.prototype._buildConfig = function () {
  var opts = this._options;
  var config = {
    schemaVersion: 2,
    chrome: opts.chrome || {},
    theme: opts.theme || {},
  };
  // Include startView only until the iframe has authenticated once. After
  // that, re-init messages (session-expired / auth-required re-fetch) must
  // not carry startView, otherwise every session refresh would redirect the
  // user back to startView regardless of where they navigated to. Unset means
  // null — the runtime's own default view, not a forced '/home'.
  if (!this._hasAuthenticated) {
    config.startView = opts.startView || null;
  }
  if (opts.privacy) config.privacy = opts.privacy;
  if (opts.locale) config.locale = opts.locale;
  // Internal: set by Querri's own configurator so its same-origin preview does
  // not write the embed cookies into the operator's real session. Omitted for
  // customers, so their payload is byte-identical to what it was.
  if (opts.preview === true) config.preview = true;
  return config;
};

// ─── postMessage Listener ─────────────────────────────────

QuerriInstance.prototype._setupMessageListener = function () {
  var self = this;

  this._messageHandler = function (e) {
    // Origin verification — and source: same-origin frames/popups from the
    // Querri origin must not be able to drive this instance's handshake.
    if (e.origin !== self._origin) return;
    if (self.iframe && e.source && e.source !== self.iframe.contentWindow) return;
    if (!e.data || !e.data.type) return;

    switch (e.data.type) {
      case 'ready':
        // Clear the ready timeout
        if (self._readyTimer) {
          clearTimeout(self._readyTimer);
          self._readyTimer = null;
        }
        // The frame answered after we had already reported it missing. Withdraw
        // the report: a host that only ever hears the error leaves it on screen
        // under a working embed, which is exactly what our own configurator did.
        if (self._timedOut) {
          self._timedOut = false;
          self._emit('recovered', {
            code: 'timeout',
            afterMs: Date.now() - self._createdAt,
          });
        }
        // Guard against duplicate 'ready' messages from the iframe
        // (e.g. embed layout + root layout both sending 'ready' before auth completes)
        if (self._iframeReady) break;
        self._iframeReady = true;
        if (self._authStrategy) self._authStrategy.onReady();
        break;

      case 'authenticated':
        // Any message from the frame proves it is alive — authenticated
        // implies ready even if the ready message itself was lost.
        self._iframeReady = true;
        // Session validated, content is rendering — emit only once
        if (!self.ready) {
          self.ready = true;
          self._hasAuthenticated = true;
          self._fetchCycleCount = 0;
          if (self._loader) {
            self._loader.remove();
            self._loader = null;
          }
          self._hideOverlay();
          self._emit('ready', {});
        }
        break;

      case 'auth-required':
        if (self._authStrategy) self._authStrategy.onAuthRequired();
        break;

      case 'session-expired':
        self.ready = false;
        clearCachedToken(self._serverUrl);
        if (self._authStrategy) self._authStrategy.onSessionExpired();
        self._emit('session-expired', {});
        break;

      case 'error':
        self._emit('error', e.data);
        break;

      case 'navigation':
        self._emit('navigation', e.data);
        break;

      case 'config-applied':
        // What the runtime ACTUALLY applied, and what it changed on the way:
        // keys it pinned, capped, coupled, renamed out of the old vocabulary,
        // or dropped. A host that sets a key we ignore should be able to find
        // out why without reading our source.
        self._emit('config', e.data);
        break;

      case 'send-prompt-result':
        var entry = null;
        if (e.data.promptId != null) {
          for (var pi = 0; pi < self._pendingPrompts.length; pi++) {
            if (self._pendingPrompts[pi].id === e.data.promptId) {
              entry = self._pendingPrompts.splice(pi, 1)[0];
              break;
            }
          }
        } else if (self._pendingPrompts.length) {
          // Runtime predating the promptId echo: ordered delivery, FIFO.
          entry = self._pendingPrompts.shift();
        }
        if (entry) {
          entry.resolve({ ok: e.data.ok === true, message: e.data.message || '' });
        }
        break;

      case 'resize':
        if (self._options.autoHeight && self.iframe && e.data.height > 0) {
          self.iframe.style.height = e.data.height + 'px';
        }
        self._emit('resize', e.data);
        break;

      case 'chat':
        // chat:sent / chat:finished / chat:error, so a host can show its own
        // progress affordance instead of guessing from the iframe's silence.
        self._emit('chat', e.data);
        break;
    }
  };

  window.addEventListener('message', this._messageHandler);
};

// ─── Events ───────────────────────────────────────────────

QuerriInstance.prototype.on = function (event, callback) {
  if (!this._listeners[event]) this._listeners[event] = [];
  this._listeners[event].push(callback);
  return this;
};

QuerriInstance.prototype.off = function (event, callback) {
  if (!this._listeners[event]) return this;
  this._listeners[event] = this._listeners[event].filter(function (cb) {
    return cb !== callback;
  });
  return this;
};

QuerriInstance.prototype._emit = function (event, data) {
  var cbs = this._listeners[event];
  if (!cbs) return;
  for (var i = 0; i < cbs.length; i++) {
    try {
      cbs[i](data);
    } catch (e) {
      console.error('QuerriEmbed: error in ' + event + ' handler:', e);
    }
  }
};

QuerriInstance.prototype._emitError = function (code, message, detail) {
  var payload = { code: code, message: message };
  if (detail) payload.detail = detail;
  this._emit('error', payload);
};

QuerriInstance.prototype._deferError = function (code, message) {
  var self = this;
  setTimeout(function () {
    if (!self._destroyed) self._emitError(code, message);
  }, 0);
};

// ─── Public Methods ───────────────────────────────────────

/**
 * Replace the chrome/theme this embed is using.
 *
 * The whole object, not a patch: the runtime REPLACES the customer layer, so a
 * key you set back to its default returns to the default. Merging would leave
 * it stuck at the old value.
 *
 * Safe before `ready` — the config is folded into the init that follows.
 */
QuerriInstance.prototype.updateConfig = function (config) {
  if (!config || typeof config !== 'object') return this;
  if (config.chrome) this._options.chrome = config.chrome;
  if (config.theme) this._options.theme = config.theme;
  if (config.privacy) this._options.privacy = config.privacy;
  if (typeof config.locale === 'string') this._options.locale = config.locale;
  if (config.preview !== undefined) this._options.preview = !!config.preview;
  // Gate on the FRAME being alive, not on auth: the runtime accepts
  // updateConfig as soon as its bridge installs, and gating on `ready`
  // (= authenticated) silently swallowed any update landing in the
  // init->authenticated window — the wrappers' content-compare then
  // suppressed the retry, losing the change permanently. Before the frame
  // says `ready`, the mutation above is enough: init carries it.
  if (this._iframeReady) {
    this._sendToIframe({ type: 'updateConfig', config: this._buildConfig() });
  }
  return this;
};

/**
 * Put text in the embed's composer, and optionally send it.
 * Resolves with `{ ok, message }` — `ok: false` when the current view has no
 * prompt input, which is a real answer rather than silence.
 */
QuerriInstance.prototype.sendPrompt = function (text, options) {
  var self = this;
  var opts = options || {};
  if (!this.ready) {
    return Promise.resolve({ ok: false, message: 'Embed is not ready yet' });
  }
  if (typeof text !== 'string' || !text.trim()) {
    return Promise.resolve({ ok: false, message: 'sendPrompt requires a non-empty string' });
  }
  return new Promise(function (resolve) {
    // Correlated by promptId where the runtime echoes it; FIFO otherwise
    // (ordered postMessage delivery, one result per prompt). The id is what
    // keeps the queue aligned even if a runtime bug ever drops one result —
    // without it, every later answer would resolve the wrong call forever.
    var id = ++self._promptSeq;
    self._pendingPrompts.push({ id: id, resolve: resolve });
    self._sendToIframe({
      type: 'send-prompt',
      promptId: id,
      text: text,
      autoSubmit: opts.autoSubmit === true,
    });
  });
};

QuerriInstance.prototype.destroy = function () {
  this._destroyed = true;
  this.ready = false;
  this._iframeReady = false;
  this._timedOut = false;
  // Settle, never strand: a wrapper remount destroys the instance while a
  // sendPrompt may still be awaited — an un-settled promise hangs forever.
  var pending = this._pendingPrompts;
  this._pendingPrompts = [];
  for (var i = 0; i < pending.length; i++) {
    pending[i].resolve({ ok: false, message: 'Embed destroyed before the prompt was delivered' });
  }

  if (this._messageHandler) {
    window.removeEventListener('message', this._messageHandler);
    this._messageHandler = null;
  }
  if (this._popupMessageHandler) {
    window.removeEventListener('message', this._popupMessageHandler);
    this._popupMessageHandler = null;
  }
  if (this._pollTimer) {
    clearInterval(this._pollTimer);
    this._pollTimer = null;
  }
  if (this._readyTimer) {
    clearTimeout(this._readyTimer);
    this._readyTimer = null;
  }
  if (this._retryTimer) {
    clearTimeout(this._retryTimer);
    this._retryTimer = null;
  }
  this._fetchInFlight = false;
  this._fetchCycleCount = 0;
  if (this._popup && !this._popup.closed) {
    this._popup.close();
    this._popup = null;
  }
  if (this.iframe) {
    this.iframe.remove();
    this.iframe = null;
  }
  if (this._overlay) {
    this._overlay.remove();
    this._overlay = null;
  }
  if (this._loader) {
    this._loader.remove();
    this._loader = null;
  }

  this._listeners = {};
};

// ─── Public API ───────────────────────────────────────────

export var QuerriEmbed = {
  create: function (selectorOrElement, options) {
    if (typeof document === 'undefined') {
      throw new Error(
        'QuerriEmbed.create() requires a browser environment. ' +
        'This code should only run on the client side. ' +
        'In Next.js, wrap in useEffect. In Nuxt, use onMounted.'
      );
    }

    if (!options || !options.serverUrl) {
      throw new Error('QuerriEmbed: serverUrl is required');
    }
    if (!options.auth) {
      throw new Error('QuerriEmbed: auth is required');
    }

    if (typeof options.serverUrl === 'string' && !/^https?:\/\/.+/.test(options.serverUrl)) {
      console.warn(
        "QuerriEmbed: serverUrl doesn't look like a URL. Expected 'https://...' but got '" +
        options.serverUrl + "'"
      );
    }

    var container = resolveElement(selectorOrElement);
    return new QuerriInstance(container, options);
  },

  version: '1.1.0',
};

export default QuerriEmbed;
